import { Button, Chip, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { compact, isEmpty, map, omit, values } from 'lodash';

import { SortKey } from 'enums/common.enum';
import { rxIntakeRequestStatus } from 'enums/request.enum';
import { updateRxIntakeRequestFilter } from 'store/thunks/workQueueThunks';
import { casingFormat } from 'common/utils';
import { getPartnerPractices } from 'store/thunks/partnerThunks';

const RxInTakeRequestsFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.rxIntakeRequests?.filter);

  const partners = useSelector(
    ({ auth, partner: partnerStore }) => partnerStore[auth.currentTenantId]
  );

  const partnerOptions = partners?.map(({ partnerName, partnerUuid }) => ({
    value: partnerUuid,
    label: casingFormat(partnerName),
  }));

  const practices =
    filter?.partnerIds && filter?.practiceIds
      ? compact(
          filter.partnerIds.flatMap((partnerId) => {
            const partner = partners?.find((p) => p.partnerUuid === partnerId);
            return (
              partner?.partnerPractices?.filter((practice) =>
                filter.practiceIds.includes(practice.practiceId)
              ) || []
            );
          })
        )
      : [];

  const practiceOptions = practices.map(({ practiceId, practiceName }) => ({
    value: practiceId,
    label: casingFormat(practiceName),
  }));

  const isPracticeCleared = filter?.practiceIds && isEmpty(practiceOptions);

  useEffect(() => {
    if (isPracticeCleared) {
      dispatch(getPartnerPractices({ partnerIds: [filter.partnerId] }));
    }
  }, [dispatch, isPracticeCleared, filter]);

  const removeAllFilter = () =>
    dispatch(
      updateRxIntakeRequestFilter({
        filter: {},
      })
    );

  const removeFilter = (filterName) => () => {
    dispatch(
      updateRxIntakeRequestFilter({
        filter: {
          ...filter,
          [filterName]: undefined,
          ...(filterName === 'partnerIds' && { practiceIds: undefined }),
        },
      })
    );
  };

  const getDateRange = (dateRange) => {
    const dates = map(dateRange, (date) => {
      const [year, month, day] = date.split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MM/dd/yyyy');
    });

    return dates.length === 1 ? `${dates[0]}` : `${dates[0]} - ${dates[1]}`;
  };

  return (
    <Stack
      spacing={1}
      direction='row'
      flexWrap='wrap'
      rowGap={1}
      alignItems='center'
      sx={{ maxWidth: '80rem' }}
    >
      {filter.patientName && (
        <Chip
          label={`Name = ${filter.patientName}`}
          onDelete={removeFilter(SortKey.PATIENT_NAME)}
        />
      )}

      {filter.medicationName && (
        <Chip
          label={`Drug Name = ${filter.medicationName}`}
          onDelete={removeFilter(SortKey.MEDICATION_NAME)}
        />
      )}

      {filter.requestStatus && (
        <Chip
          label={`Status = ${rxIntakeRequestStatus[filter.requestStatus]}`}
          onDelete={removeFilter(SortKey.REQUEST_STATUS)}
        />
      )}

      {filter.partnerAccount && (
        <Chip
          label={`Partner Account = ${filter.partnerAccount}`}
          onDelete={removeFilter(SortKey.PARTNER_ACCOUNT)}
        />
      )}

      {filter.practiceName && (
        <Chip
          label={`Practice = ${filter.practiceName}`}
          onDelete={removeFilter(SortKey.PRACTICE_NAME)}
        />
      )}

      {filter.scriptId && (
        <Chip label={`Script Id = ${filter.scriptId}`} onDelete={removeFilter(SortKey.SCRIPT_ID)} />
      )}

      {filter.documentId && (
        <Chip
          label={`Docutrack Number = ${filter.documentId}`}
          onDelete={removeFilter(SortKey.DOCUTRACK_ID)}
        />
      )}

      {filter?.createdAt && (
        <Chip
          label={`Date Range = ${getDateRange(filter.createdAt)}`}
          variant='contained'
          onDelete={removeFilter(SortKey.CREATED_AT)}
        />
      )}

      {filter?.partnerIds && (
        <Chip
          label={`Partner Name = ${partnerOptions
            ?.filter((opt) => filter.partnerIds?.includes(opt.value))
            .map((opt) => opt.label)
            .join(',  ')}`}
          variant='contained'
          onDelete={removeFilter(SortKey.PARTNER_IDS)}
        />
      )}

      {filter?.partnerIds && filter?.practiceIds && (
        <Chip
          label={`Practice Name = ${practiceOptions
            ?.filter((opt) => filter.practiceIds?.includes(opt.value))
            .map((opt) => opt.label)
            .join(', ')}`}
          variant='contained'
          onDelete={removeFilter(SortKey.PRACTICE_IDS)}
        />
      )}

      {!isEmpty(compact(values(omit(filter, 'taggedForReview')))) && (
        <Button onClick={removeAllFilter}>Remove All</Button>
      )}
    </Stack>
  );
};

export default RxInTakeRequestsFilterStatusBar;
